import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trash,
  UserPlus,
  MagnifyingGlass,
  ClipboardText,
  Eye,
  PencilSimple,
  FileText,
  Archive,
  Microphone,
  Stop,
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useLanguage } from '../context/LanguageContext';
import OrderPrintView, { VoorbladDocument, printLogoSrc } from '../components/OrderPrintView';
import OrderFilesPanel from '../components/OrderFilesPanel';
import NetworkFilesPanel from '../components/NetworkFilesPanel';
import WerkvoorbereidingForm from '../components/WerkvoorbereidingForm';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { API, AXIOS_SKIP_HTTP_CACHE, ORDERS_LIST_PARAMS } from '../config';
import { useDeepgramDictation } from '../hooks/useDeepgramDictation';
import { orderMatchesGlobalSearch } from '../utils/listSearch';
import {
  getDefaultOrderViewTab,
  getOpleverbonAttention,
  getServicebonAttentionKind,
  getPendingOfficeAttention,
  hasUnreadWorkerNotes,
  isOrderInMontageWorkList,
  orderNeedsAttention,
} from '../utils/orderAttention';
import { formatServicebonRoundLabel } from '../utils/servicebonLabel';
import { openSlipPrintWindow } from '../utils/slipPrint';
import { isAdmin } from '../utils/roles';
import { useAuth } from '../context/AuthContext';

/** Servicebon legal text (independent of opleverbon) — see mobile app. */
const SERVICEBON_DECLARATION_TEXT = `*Geachte heer/mevrouw,*

Dit serviceformulier is opgesteld naar aanleiding van een eerdere oplevering, waarbij nog enkele restpunten of aanvullende werkzaamheden openstonden. Met deze servicebon worden de nog uit te voeren werkzaamheden zorgvuldig vastgelegd en afgehandeld conform de oorspronkelijke opdracht, de technische tekening(en), offerte/orderbevestiging en de toepasselijke algemene voorwaarden van VMK Kozijnen BV.

De op deze servicebon vermelde werkzaamheden vloeien voort uit de eerder ondertekende opleverbon en betreffen uitsluitend de nog openstaande restpunten, servicewerkzaamheden of aanvullende correcties zoals reeds overeengekomen.

Na uitvoering van de hieronder omschreven werkzaamheden kan door ondertekening worden bevestigd dat de betreffende restpunten naar behoren zijn afgehandeld en dat VMK Kozijnen BV daarmee aan haar serviceverplichting met betrekking tot deze punten heeft voldaan.

Indien naar aanleiding van de eerdere oplevering bedragen op de totaalfactuur zijn ingehouden, dienen deze na correcte afronding van de op deze servicebon vermelde werkzaamheden per omgoende betaalbaar te worden gesteld.

Eventuele aanvullende constateringen of nieuwe serviceverzoeken dienen schriftelijk te worden gemeld via service@vmkkozijnen.nl . Nieuwe serviceverzoeken of aanvullende meldingen ontslaan u niet van uw bestaande betalingsverplichtingen.`;

const SERVICEBON_DECLARATION_TEXT_EN = `*Dear Sir/Madam,*

This service form follows an earlier handover where some remaining items or additional work were still open. With this service form, the work still to be carried out is carefully recorded and completed in line with the original assignment, the technical drawing(s), quotation/order confirmation and the applicable general terms and conditions of VMK Kozijnen BV.

The work stated on this service form arises from the previously signed completion slip and relates solely to outstanding remaining items, service work or additional corrections as already agreed.

After the work described below has been carried out, signing confirms that the relevant remaining items have been properly resolved and that VMK Kozijnen BV has thereby fulfilled its service obligation regarding those items.

If amounts were withheld from the total invoice following the earlier handover, these must be made payable without delay after the work stated on this service form has been properly completed.

Any additional findings or new service requests must be reported in writing to service@vmkkozijnen.nl. New service requests or additional reports do not release you from your existing payment obligations.`;

const OPLEVERBON_DECLARATION_TEXT = `Dit opleverformulier is opgesteld om de oplevering van de door u bestelde producten en uitgevoerde werkzaamheden zorgvuldig vast te leggen. Hierbij wordt beoordeeld in hoeverre de levering en montage zijn uitgevoerd conform de overeengekomen opdracht, de technische tekening(en), offerte/orderbevestiging en de toepasselijke algemene voorwaarden van VMK Kozijnen BV.

Indien er bij oplevering nog restpunten of aanvullende werkzaamheden openstaan, dienen deze hieronder duidelijk en volledig te worden omschreven. VMK Kozijnen BV verplicht zich om de vastgelegde restpunten binnen een redelijke en overeengekomen termijn alsnog correct af te handelen.

Door ondertekening van dit formulier verklaart u dat, behoudens de hieronder schriftelijk vermelde restpunten:
* de geleverde producten deugdelijk en conform opdracht zijn geleverd;
* de montagewerkzaamheden naar behoren zijn uitgevoerd;
* de oplevering heeft plaatsgevonden volgens de afspraken zoals vastgelegd in de offerte/orderbevestiging, technische stukken en algemene voorwaarden;
* eventuele zichtbare gebreken en opmerkingen op dit formulier zijn vermeld.

Eventuele mondelinge afspraken die tijdens de montage of oplevering worden gemaakt tussen u en onze monteurs zijn uitsluitend bindend indien deze schriftelijk op dit formulier worden vermeld.`;

const OPLEVERBON_DECLARATION_TEXT_EN = `This completion form is prepared to carefully record the completion of the products ordered by you and the work performed. It assesses to what extent delivery and installation have been carried out in accordance with the agreed assignment, technical drawing(s), quotation/order confirmation and the applicable general terms and conditions of VMK Kozijnen BV.

If there are still open items or additional works at completion, these must be clearly and fully described below. VMK Kozijnen BV undertakes to properly resolve the recorded open items within a reasonable and agreed period.

By signing this form, you declare that, except for any open items listed below in writing:
* the delivered products have been supplied properly and in accordance with the order;
* the installation works have been performed properly;
* completion has taken place in line with the agreements set out in the quotation/order confirmation, technical documents and general terms and conditions;
* any visible defects and remarks have been noted on this form.

Any verbal agreements made during installation or completion between you and our installers are only binding if recorded on this form in writing.`;

/** API may expose JSON as `client_signature_json` or (legacy) `client_signature`. */
function getOrderClientSignature(order) {
  if (!order || typeof order !== 'object') return null;
  const raw = order.client_signature_json ?? order.client_signature;
  return raw && typeof raw === 'object' ? raw : null;
}

/** Active order + current servicebon round not signed on mobile — office can edit work text. */
function isFullyClosedOrder(order) {
  return (
    String(order?.mobile_work_status || '') === 'completed' &&
    !order?.pending_closed_release
  );
}

function canEditServicebonWorkOnWeb(order) {
  if (!order || isFullyClosedOrder(order)) return false;
  const sig = getOrderClientSignature(order);
  const sb = sig?.servicebon;
  if (sb && typeof sb === 'object' && String(sb.data || '').trim()) return false;
  return true;
}

function getServicebonHistoryEntries(sig) {
  if (!sig || typeof sig !== 'object') return [];
  const h = sig.servicebon_history;
  return Array.isArray(h) ? h.filter((x) => x && typeof x === 'object') : [];
}

function parseFinancialHoldPercentValue(sig) {
  if (!sig || typeof sig !== 'object') return null;
  const raw = sig.financial_hold_percent ?? sig.financial_hold_pct;
  if (raw == null || raw === '') return null;
  const n = parseInt(String(raw).replace(/[^\d]/g, ''), 10);
  if (!Number.isFinite(n) || n < 1 || n > 50) return null;
  return n;
}

/** Maps saved signature to nee | 10 | custom (+ percent 1–50) for Opleverbon print. */
function resolveFinancialHoldState(sig) {
  if (!sig || typeof sig !== 'object') return { key: null, percent: null };
  const h = String(sig.financial_hold ?? sig.financial_hold_choice ?? '').trim().toLowerCase();
  if (h === 'nee' || h === 'none' || h === 'no' || h === '0') return { key: 'nee', percent: null };
  if (h === '10' || h === '10pct' || h === '10%') return { key: '10', percent: null };
  if (h === 'custom') {
    const p = parseFinancialHoldPercentValue(sig);
    return { key: p != null ? 'custom' : null, percent: p };
  }
  if (h === '20' || h === '20pct' || h === '20%') return { key: 'custom', percent: 20 };
  const amt = String(sig.financial_amount ?? '').trim();
  const ml = amt.toLowerCase();
  const m = ml.match(/(\d+)\s*%/);
  if (m) {
    const v = parseInt(m[1], 10);
    if (v === 10) return { key: '10', percent: null };
    if (v >= 1 && v <= 50) return { key: 'custom', percent: v };
  }
  if (/\b20\b|20\s*%/.test(ml)) return { key: 'custom', percent: 20 };
  if (/\b10\b|10\s*%/.test(ml)) return { key: '10', percent: null };
  if (/nee|no|geen|^0$|^n\/a$/i.test(ml)) return { key: 'nee', percent: null };
  return { key: null, percent: null };
}

function buildOrderAttentionTitle(order, t) {
  const parts = [];
  if (hasUnreadWorkerNotes(order)) parts.push(t('tabNotifyWorkerNote'));
  if (getPendingOfficeAttention(order)) parts.push(t('tabNotifyOfficeBalance'));
  const sbKind = getServicebonAttentionKind(order);
  if (sbKind === 'need_web_text') parts.push(t('tabNotifyServicebonNeedWebText'));
  if (sbKind === 'signed_mobile') parts.push(t('tabNotifyServicebonSigned'));
  if (getOpleverbonAttention(order)) parts.push(t('tabNotifyOpleverSigned'));
  return parts.join(' · ');
}

function buildNotesTabAttentionTitle(order, t) {
  const parts = [];
  if (hasUnreadWorkerNotes(order)) parts.push(t('tabNotifyWorkerNote'));
  if (getPendingOfficeAttention(order)) parts.push(t('tabNotifyOfficeBalance'));
  return parts.length ? `${t('notesLabel')}. ${parts.join(' · ')}` : t('notesLabel');
}

const OrdersPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canMarkComplete = isAdmin(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const viewOrderIdFromUrl = searchParams.get('view');
  const viewingFromClosed = searchParams.get('from') === 'closed';
  const viewingFromService =
    searchParams.get('from') === 'service' ||
    searchParams.get('from') === 'service-archive';
  const viewingFromPlanning = searchParams.get('from') === 'planning';
  const tabFromUrl = searchParams.get('tab');
  const editOrderIdFromUrl = searchParams.get('edit');
  const [orders, setOrders] = useState([]);
  const [teams, setTeams] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAssign, setShowAssign] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [viewTab, setViewTab] = useState('voorblad');
  /** null = active servicebon; number = index in `servicebon_history` (standalone archived A4). */
  const [servicebonArchiveIndex, setServicebonArchiveIndex] = useState(null);
  const [deletePhotoUrl, setDeletePhotoUrl] = useState(null);
  const [servicebonWorkDraft, setServicebonWorkDraft] = useState('');
  const [servicebonDraftBaseline, setServicebonDraftBaseline] = useState('');
  const [servicebonDraftSavedOnce, setServicebonDraftSavedOnce] = useState(false);
  const [servicebonDraftSaving, setServicebonDraftSaving] = useState(false);
  const servicebonWorkTextareaRef = useRef(null);
  const servicebonWorkDraftRef = useRef('');
  const servicebonDraftOrderIdRef = useRef(null);
  const servicebonWorkBlurTimerRef = useRef(null);
  const [assignData, setAssignData] = useState({ team_id: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const printRef = useRef(null);
  const opleverbonPrintRef = useRef(null);
  const handlePrintSlipRef = useRef(() => {});

  servicebonWorkDraftRef.current = servicebonWorkDraft;

  const servicebonVoice = useDeepgramDictation({
    onDraftChange: setServicebonWorkDraft,
    getDraft: () => servicebonWorkDraftRef.current,
  });

  const showServicebonVoiceMic =
    showPrintView &&
    viewTab === 'servicebon' &&
    viewOrder &&
    !viewingFromClosed &&
    canEditServicebonWorkOnWeb(viewOrder) &&
    servicebonArchiveIndex == null;

  const stopServicebonVoiceRef = useRef(servicebonVoice.stop);
  stopServicebonVoiceRef.current = servicebonVoice.stop;

  useEffect(() => {
    stopServicebonVoiceRef.current();
    return () => {
      stopServicebonVoiceRef.current();
      if (servicebonWorkBlurTimerRef.current) {
        clearTimeout(servicebonWorkBlurTimerRef.current);
      }
    };
  }, [viewOrder?.id, viewTab, servicebonArchiveIndex]);

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, teamsRes, workersRes] = await Promise.all([
        axios.get(`${API}/orders`, {
          withCredentials: true,
          ...AXIOS_SKIP_HTTP_CACHE,
          ...ORDERS_LIST_PARAMS,
        }),
        axios.get(`${API}/teams`, { withCredentials: true }),
        axios.get(`${API}/workers`, { withCredentials: true }),
      ]);
      setOrders(ordersRes.data);
      setTeams(teamsRes.data);
      setWorkers(workersRes.data);
    } catch (error) {
      toast.error(t('failedFetchData'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    setServicebonArchiveIndex(null);
  }, [viewOrder?.id]);

  useEffect(() => {
    if (loading) return undefined;
    const intervalMs = 60000;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void fetchData();
    }, intervalMs);
    const onVis = () => {
      if (document.visibilityState === 'visible') void fetchData();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [loading, fetchData]);

  useEffect(() => {
    if (!showPrintView || !viewOrder?.id) return;
    const fresh = orders.find((o) => String(o.id) === String(viewOrder.id));
    if (!fresh) return;
    const dirty = servicebonWorkDraft !== servicebonDraftBaseline;
    if (dirty) return;
    // Light list polls must not wipe full signature/photo payloads loaded via /orders/:id
    setViewOrder((prev) => {
      if (!prev || String(prev.id) !== String(fresh.id)) return prev;
      if (fresh._light) {
        const {
          client_signature_json: _sig,
          worker_notes_json: _notes,
          mobile_photos_json: _photos,
          _light: _flag,
          ...meta
        } = fresh;
        return {
          ...prev,
          ...meta,
          client_signature_json: prev.client_signature_json,
          worker_notes_json: prev.worker_notes_json,
          mobile_photos_json: prev.mobile_photos_json,
        };
      }
      return { ...prev, ...fresh };
    });
  }, [orders, showPrintView, viewOrder?.id, servicebonWorkDraft, servicebonDraftBaseline]);

  useEffect(() => {
    if (!viewOrderIdFromUrl || loading) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/orders/${viewOrderIdFromUrl}`, {
          withCredentials: true,
          ...AXIOS_SKIP_HTTP_CACHE,
        });
        if (cancelled) return;
        setViewOrder(res.data);
        setShowPrintView(true);
        const tab = searchParams.get('tab');
        if (
          tab &&
          ['voorblad', 'opleverbon', 'servicebon', 'files', 'notes'].includes(tab)
        ) {
          setViewTab(tab);
        } else {
          setViewTab(getDefaultOrderViewTab(res.data));
        }
      } catch {
        if (!cancelled) toast.error(t('failedFetchData'));
      }
      if (!cancelled) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('view');
          return next;
        }, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewOrderIdFromUrl, loading, setSearchParams, searchParams, t]);

  // Sidebar nav to bare /orders must close a lingering servicebon/opleverbon overlay.
  useEffect(() => {
    if (viewOrderIdFromUrl || editOrderIdFromUrl) return;
    if (searchParams.get('from') || searchParams.get('tab')) return;
    setShowPrintView(false);
    setViewOrder(null);
    setViewTab('voorblad');
    setServicebonArchiveIndex(null);
  }, [viewOrderIdFromUrl, editOrderIdFromUrl, searchParams]);

  useEffect(() => {
    if (
      tabFromUrl &&
      ['voorblad', 'opleverbon', 'servicebon', 'files', 'notes'].includes(tabFromUrl) &&
      showPrintView
    ) {
      setViewTab(tabFromUrl);
    }
  }, [tabFromUrl, showPrintView, viewOrder?.id]);

  useEffect(() => {
    if (!showPrintView || !viewOrder?.id || viewTab !== 'notes' || !hasUnreadWorkerNotes(viewOrder)) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.put(
          `${API}/orders/${viewOrder.id}/worker-notes-seen`,
          {},
          { withCredentials: true, ...AXIOS_SKIP_HTTP_CACHE },
        );
        if (cancelled || !data?.id) return;
        setViewOrder((prev) => (prev && String(prev.id) === String(data.id) ? { ...prev, ...data } : prev));
        setOrders((prev) =>
          prev.map((o) => (String(o.id) === String(data.id) ? { ...o, ...data } : o)),
        );
      } catch {
        /* polling will retry; dot stays until seen */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showPrintView, viewOrder?.id, viewTab, viewOrder?.worker_notes_json, viewOrder?.worker_notes_office_seen_at]);

  useEffect(() => {
    if (!editOrderIdFromUrl || loading) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/orders/${editOrderIdFromUrl}`, {
          withCredentials: true,
          ...AXIOS_SKIP_HTTP_CACHE,
        });
        if (cancelled) return;
        setEditOrder(res.data);
      } catch {
        if (!cancelled) toast.error(t('failedFetchData'));
      }
      if (!cancelled) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('edit');
          return next;
        }, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editOrderIdFromUrl, loading, setSearchParams, t]);

  useEffect(() => {
    if (!viewOrder || !showPrintView) {
      setServicebonWorkDraft('');
      setServicebonDraftBaseline('');
      setServicebonDraftSavedOnce(false);
      servicebonDraftOrderIdRef.current = null;
      return;
    }
    if (servicebonArchiveIndex != null) {
      return;
    }
    if (!canEditServicebonWorkOnWeb(viewOrder)) {
      setServicebonWorkDraft('');
      setServicebonDraftBaseline('');
      setServicebonDraftSavedOnce(false);
      servicebonDraftOrderIdRef.current = null;
      return;
    }
    const oid = String(viewOrder.id ?? '');
    const orderChanged = servicebonDraftOrderIdRef.current !== oid;
    if (orderChanged) {
      servicebonDraftOrderIdRef.current = oid;
      setServicebonDraftSavedOnce(false);
    }
    const dirty = servicebonWorkDraft !== servicebonDraftBaseline;
    if (!orderChanged && dirty) {
      return;
    }
    const sig = getOrderClientSignature(viewOrder);
    const sb = sig && typeof sig.servicebon === 'object' && sig.servicebon != null ? sig.servicebon : null;
    const from = sb ? String(sb.pending_work || '') : '';
    setServicebonWorkDraft(from);
    setServicebonDraftBaseline(from);
    if (from.trim().length > 0) {
      setServicebonDraftSavedOnce(true);
    }
  }, [viewOrder, showPrintView, servicebonWorkDraft, servicebonDraftBaseline, servicebonArchiveIndex]);

  const handleViewOrder = (order) => {
    setSearchParams({
      view: String(order.id),
      tab: getDefaultOrderViewTab(order),
    });
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Werkvoorbereiding - ${viewOrder?.client_name}</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 8mm 10mm;
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: #1a1a1a;
            line-height: 1.4;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          table { border-collapse: collapse; }
          @media print {
            body { padding: 0; margin: 0; }
            .no-print { display: none !important; }
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handlePrintOpleverbon = () => {
    const printContent = opleverbonPrintRef.current;
    if (!printContent) return;
    const sig = getOrderClientSignature(viewOrder);
    const hist = viewTab === 'servicebon' ? getServicebonHistoryEntries(sig) : [];
    const isArchivePrint =
      viewTab === 'servicebon' &&
      servicebonArchiveIndex != null &&
      servicebonArchiveIndex >= 0 &&
      servicebonArchiveIndex < hist.length;
    const printTitle =
      viewTab === 'servicebon'
        ? isArchivePrint
          ? `${t('servicebonLabel')} (${formatServicebonRoundLabel(
              hist[servicebonArchiveIndex],
              servicebonArchiveIndex + 1,
              viewOrder,
            )}) — ${viewOrder?.client_name || ''}`
          : `${t('servicebonLabel')} (${formatServicebonRoundLabel(
              sig?.servicebon,
              hist.length + 1,
              viewOrder,
            )}) — ${viewOrder?.client_name || ''}`
        : `${t('completionSlipLabel')} — ${viewOrder?.client_name || ''}`;
    openSlipPrintWindow({
      title: printTitle,
      html: printContent.outerHTML,
      isServicebon: viewTab === 'servicebon',
    });
  };
  handlePrintSlipRef.current = handlePrintOpleverbon;

  useEffect(() => {
    const isSlipTab = viewTab === 'opleverbon' || viewTab === 'servicebon';
    if (!showPrintView || !isSlipTab) return undefined;

    const onKeyDown = (event) => {
      if (event.key !== 'p' && event.key !== 'P') return;
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      event.stopPropagation();
      handlePrintSlipRef.current();
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [showPrintView, viewTab]);

  const handleConfirmDeleteMobilePhoto = async () => {
    const url = deletePhotoUrl;
    if (!url || !viewOrder?.id) return;
    const id = viewOrder.id;
    setDeletePhotoUrl(null);
    try {
      const res = await axios.delete(`${API}/orders/${id}/mobile-photos`, {
        params: { url },
        withCredentials: true,
      });
      setViewOrder(res.data);
      toast.success(t('photoRemoved'));
    } catch {
      toast.error(t('photoDeleteFailed'));
    }
  };

  const handleServicebonVoiceToggle = async () => {
    try {
      await servicebonVoice.toggle();
    } catch (e) {
      const name = e?.name || '';
      const msg = String(e?.message || '');
      if (name === 'NotAllowedError' || msg.includes('microphone')) {
        toast.error(t('servicebonVoiceMicDenied'));
      } else if (msg.includes('not_authenticated')) {
        toast.error(t('servicebonVoiceAuthFailed'));
      } else {
        toast.error(t('servicebonVoiceFailed'));
      }
    }
  };

  const servicebonVoiceControls =
    showServicebonVoiceMic ? (
      <div className="servicebon-voice-controls no-print" role="group" aria-label={t('servicebonVoiceStart')}>
        <button
          type="button"
          className={`servicebon-voice-fab${servicebonVoice.listening ? ' is-listening' : ''}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => void handleServicebonVoiceToggle()}
          disabled={servicebonVoice.busy}
          aria-label={
            servicebonVoice.listening ? t('servicebonVoiceStop') : t('servicebonVoiceStart')
          }
          title={servicebonVoice.listening ? t('servicebonVoiceStop') : t('servicebonVoiceStart')}
        >
          {servicebonVoice.listening ? (
            <Stop size={28} weight="fill" aria-hidden />
          ) : (
            <Microphone size={28} weight="fill" aria-hidden />
          )}
        </button>
        {servicebonVoice.listening && (
          <span className="servicebon-voice-status" role="status">
            {t('servicebonVoiceListening')}
          </span>
        )}
      </div>
    ) : null;

  const handleSaveServicebonWorkDraft = async () => {
    if (!viewOrder?.id) return;
    servicebonVoice.stop();
    try {
      setServicebonDraftSaving(true);
      const res = await axios.put(
        `${API}/orders/${viewOrder.id}/servicebon-work-draft`,
        { pending_work: servicebonWorkDraft },
        { withCredentials: true, ...AXIOS_SKIP_HTTP_CACHE },
      );
      setViewOrder(res.data);
      setServicebonDraftSavedOnce(true);
      toast.success(t('servicebonWorkDraftSaved'));
      fetchData();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('servicebonWorkDraftFailed'));
    } finally {
      setServicebonDraftSaving(false);
    }
  };

  const handleAssignOrder = async () => {
    if (!assignData.team_id) {
      toast.error(t('selectTeamError'));
      return;
    }
    const selectedTeam = teams.find((team) => String(team.id) === String(assignData.team_id));
    const hasAtLeastOneMember =
      Array.isArray(selectedTeam?.members) && selectedTeam.members.length > 0;
    if (!hasAtLeastOneMember) {
      toast.error(t('selectedTeamNeedsMember'));
      return;
    }

    try {
      await axios.put(
        `${API}/orders/${selectedOrder.id}/assign`,
        assignData,
        { withCredentials: true }
      );
      toast.success(t('orderAssignedSuccess'));
      setShowAssign(false);
      setSelectedOrder(null);
      setAssignData({ team_id: '' });
      fetchData();
    } catch (error) {
      toast.error(t('failedAssignOrder'));
    }
  };

  const handleUnassign = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/unassign`, {}, { withCredentials: true });
      toast.success(t('orderUnassignedSuccess'));
      fetchData();
    } catch (error) {
      toast.error(t('failedUnassignOrder'));
    }
  };

  const handleCompleteAfterBalance = async (order) => {
    if (!window.confirm(t('confirmFinalizeOrderAfterBalance'))) return;
    try {
      await axios.put(`${API}/orders/${order.id}/complete-after-balance`, {}, { withCredentials: true });
      toast.success(t('orderFinalizedSuccess'));
      await fetchData();
      if (viewOrder && String(viewOrder.id) === String(order.id)) {
        setShowPrintView(false);
        setViewOrder(null);
        setViewTab('voorblad');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('orderFinalizeFailed'));
    }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm(t('confirmDeleteOrder'))) return;

    try {
      await axios.delete(`${API}/orders/${orderId}`, { withCredentials: true });
      toast.success(t('orderDeletedSuccess'));
      fetchData();
    } catch (error) {
      toast.error(t('failedDeleteOrder'));
    }
  };

  const handleEditOrder = (order) => {
    setEditOrder(order);
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'Unknown';
  };

  const getTeamColor = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team?.color || '#00CBA9';
  };

  const getAssignedTeamMemberNames = (order) => {
    if (!order) return '';
    const assignedTeam = teams.find((t) => String(t.id) === String(order.assigned_team_id));
    if (!assignedTeam) return '';

    if (Array.isArray(assignedTeam.member_names) && assignedTeam.member_names.length > 0) {
      return assignedTeam.member_names.join(', ');
    }
    if (Array.isArray(assignedTeam.worker_names) && assignedTeam.worker_names.length > 0) {
      return assignedTeam.worker_names.join(', ');
    }
    if (Array.isArray(assignedTeam.members) && assignedTeam.members.length > 0 && typeof assignedTeam.members[0] === 'object') {
      const names = assignedTeam.members.map((m) => m?.name).filter(Boolean);
      if (names.length > 0) return names.join(', ');
    }
    if (Array.isArray(assignedTeam.members) && assignedTeam.members.length > 0) {
      const names = assignedTeam.members
        .map((memberId) => workers.find((w) => String(w.id) === String(memberId))?.name)
        .filter(Boolean);
      if (names.length > 0) return names.join(', ');
    }

    return '';
  };

  const MARKER_COLORS = [
    { key: 'red', hex: '#ef4444' },
    { key: 'yellow', hex: '#f59e0b' },
    { key: 'green', hex: '#10b981' },
  ];

  const handleSetPlanningMarkerColor = async (order, colorKey) => {
    const current = String(order?.planning_marker_color || '').trim().toLowerCase();
    const nextColor = current === colorKey ? '' : colorKey;
    try {
      await axios.put(
        `${API}/orders/${order.id}`,
        { planning_marker_color: nextColor },
        { withCredentials: true }
      );
      setOrders((prev) =>
        prev.map((o) =>
          String(o.id) === String(order.id)
            ? { ...o, planning_marker_color: nextColor || null }
            : o
        )
      );
      if (viewOrder && String(viewOrder.id) === String(order.id)) {
        setViewOrder((prev) => (prev ? { ...prev, planning_marker_color: nextColor || null } : prev));
      }
    } catch {
      toast.error(t('failedSetPlannerColor'));
    }
  };

  const getWorkflowStatus = (order) => {
    const mobileStatus = String(order?.mobile_work_status || '').trim().toLowerCase();
    const pendingRelease = Boolean(order?.pending_closed_release);
    if (mobileStatus === 'completed' && pendingRelease) {
      return { key: 'awaiting-balance', label: t('orderStatusAwaitingBalance') };
    }
    if (mobileStatus === 'completed') {
      return { key: 'completed', label: t('statusCompleted') };
    }
    if (String(order?.status || '').trim().toLowerCase() === 'assigned') {
      return { key: 'in-progress', label: t('workflowInProgress') };
    }
    return { key: 'unassigned', label: t('unassigned') };
  };

  const activeOrders = orders.filter((order) => isOrderInMontageWorkList(order));

  const filteredOrders = activeOrders.filter((order) => orderMatchesGlobalSearch(order, searchQuery));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00CBA9] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // If viewing a specific order, show full-page print view
  if (showPrintView && viewOrder) {
    const workerNotes = Array.isArray(viewOrder.worker_notes_json) ? viewOrder.worker_notes_json : [];
    const teamMemberNames = getAssignedTeamMemberNames(viewOrder);
    const signature = getOrderClientSignature(viewOrder);
    const isServicebonView = viewTab === 'servicebon';
    const servicebonHistoryList = isServicebonView ? getServicebonHistoryEntries(signature) : [];
    const viewingServicebonArchive =
      isServicebonView &&
      servicebonArchiveIndex != null &&
      servicebonArchiveIndex >= 0 &&
      servicebonArchiveIndex < servicebonHistoryList.length;
    const slip = !isServicebonView
      ? signature
      : viewingServicebonArchive
        ? servicebonHistoryList[servicebonArchiveIndex]
        : signature?.servicebon && typeof signature.servicebon === 'object'
          ? signature.servicebon
          : null;
    const showServicebonNav = isServicebonView && servicebonHistoryList.length > 0;
    const currentServicebonRound = servicebonHistoryList.length + 1;
    const currentServicebonEntry =
      signature?.servicebon && typeof signature.servicebon === 'object' ? signature.servicebon : null;
    const currentServicebonRoundLabel = formatServicebonRoundLabel(
      currentServicebonEntry,
      currentServicebonRound,
      viewOrder,
    );
    const financialHold = resolveFinancialHoldState(signature);
    const opleverbonSignedBadge = getOpleverbonAttention(viewOrder);
    const servicebonAttentionKind = getServicebonAttentionKind(viewOrder);
    const servicebonTabBadge = Boolean(servicebonAttentionKind);
    const servicebonTabTooltip =
      servicebonAttentionKind === 'need_web_text'
        ? t('tabNotifyServicebonNeedWebText')
        : servicebonAttentionKind === 'signed_mobile'
          ? t('tabNotifyServicebonSigned')
          : undefined;
    const servicebonTabAria =
      servicebonAttentionKind === 'need_web_text'
        ? `${t('servicebonLabel')}. ${t('tabNotifyServicebonNeedWebText')}`
        : servicebonAttentionKind === 'signed_mobile'
          ? `${t('servicebonLabel')}. ${t('tabNotifyServicebonSigned')}`
          : t('servicebonLabel');
    const pendingOfficeReleaseBadge = getPendingOfficeAttention(viewOrder);
    const unreadWorkerNotesBadge = hasUnreadWorkerNotes(viewOrder);
    const notesTabBadge = pendingOfficeReleaseBadge || unreadWorkerNotesBadge;
    const notesTabTitle = buildNotesTabAttentionTitle(viewOrder, t);
    const slipFooterFieldRowHeight = isServicebonView ? 26 : 24;
    const slipFooterFieldLineHeight = isServicebonView ? 20 : 18;
    const slipFooterFieldGap = isServicebonView ? 6 : 5;
    const slipFooter = (
      <>
        <div
          className="slip-footer-fields"
          style={{ display: 'flex', flexDirection: 'column', gap: `${slipFooterFieldGap}px` }}
        >
          {[
            [t('opleverbonDate'), slip?.signed_at ? new Date(slip.signed_at).toLocaleDateString(language === 'en' ? 'en-GB' : 'nl-NL') : ''],
            [t('opleverbonPlace'), viewOrder.postcode || ''],
            ['VMK', teamMemberNames],
            [t('opleverbonCustomerName'), slip?.signer_name || ''],
          ].map(([label, value]) => (
            <div
              key={String(label)}
              className="slip-footer-field-row"
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                height: `${slipFooterFieldRowHeight}px`,
                minHeight: `${slipFooterFieldRowHeight}px`,
              }}
            >
              <span
                style={{
                  width: '130px',
                  fontSize: isServicebonView ? '12px' : '11px',
                  fontWeight: 600,
                  lineHeight: 1.2,
                  paddingBottom: '2px',
                }}
              >
                {label}:
              </span>
              <div
                style={{
                  flex: 1,
                  borderBottom: '1.5px solid #222',
                  height: `${slipFooterFieldLineHeight}px`,
                  minHeight: `${slipFooterFieldLineHeight}px`,
                  fontSize: isServicebonView ? '13px' : '12px',
                  lineHeight: `${slipFooterFieldLineHeight}px`,
                  display: 'flex',
                  alignItems: 'flex-end',
                }}
              >
                {value}
              </div>
            </div>
          ))}
        </div>
        <div
          className="slip-print-signature-row"
          style={{
            display: 'flex',
            alignItems: 'stretch',
            minHeight: isServicebonView ? '78px' : '64px',
            marginTop: `${slipFooterFieldGap}px`,
          }}
        >
          <span style={{ width: '130px', fontSize: isServicebonView ? '12px' : '11px', fontWeight: 600, display: 'flex', alignItems: 'flex-end' }}>
            {t('opleverbonClientSignature')}:
          </span>
          <div style={{ flex: 1, borderBottom: '1.5px solid #222', position: 'relative' }}>
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, borderLeft: '1px solid #222' }} />
            <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, borderRight: '1px solid #222' }} />
            {slip?.data && (
              <img
                src={slip.data}
                alt="Signature"
                style={{
                  position: 'absolute',
                  left: '35%',
                  right: '35%',
                  bottom: '3px',
                  width: '30%',
                  height: isServicebonView ? '56px' : '48px',
                  objectFit: 'contain',
                }}
              />
            )}
          </div>
        </div>
      </>
    );
    const slipBoxStyle = { border: '2px solid #2b2b2b', boxSizing: 'border-box' };
    const printCheckbox = (checked) => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '12px',
      height: '12px',
      border: checked ? '1.5px solid #00CBA9' : '1.5px solid #222',
      marginRight: '8px',
      flexShrink: 0,
      fontSize: '9px',
      fontWeight: 700,
      lineHeight: 1,
      color: checked ? '#fff' : '#222',
      backgroundColor: checked ? '#00CBA9' : 'transparent',
    });
    return (
      <div
        data-testid={
          viewingFromClosed
            ? 'closed-orders-view'
            : viewingFromService
              ? 'service-orders-view'
              : 'orders-page'
        }
        className="animate-fadeIn"
      >
        <OrderPrintView
          ref={printRef}
          order={viewOrder}
          hideDocument
          showDefaultPrintButton
          onClose={() => {
            if (viewingFromClosed) {
              navigate('/closed-orders');
              return;
            }
            if (viewingFromPlanning) {
              navigate('/planning');
              return;
            }
            if (viewingFromService) {
              navigate('/service');
              return;
            }
            setShowPrintView(false);
            setViewOrder(null);
            setViewTab('voorblad');
            setServicebonArchiveIndex(null);
            setSearchParams({}, { replace: true });
          }}
          onPrint={handlePrint}
          centerActions={
            <div className="order-tabs" role="tablist" aria-label="Order view tabs">
              <button
                type="button"
                role="tab"
                aria-selected={viewTab === 'voorblad'}
                className="order-tab"
                onClick={() => setViewTab('voorblad')}
              >
                {t('voorbladWerkvoorbereiding')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={viewTab === 'opleverbon'}
                className={`order-tab${opleverbonSignedBadge ? ' order-tab--has-badge' : ''}`}
                title={opleverbonSignedBadge ? t('tabNotifyOpleverSigned') : undefined}
                aria-label={
                  opleverbonSignedBadge
                    ? `${t('completionSlipLabel')}. ${t('tabNotifyOpleverSigned')}`
                    : t('completionSlipLabel')
                }
                onClick={() => setViewTab('opleverbon')}
              >
                {t('completionSlipLabel')}
                {opleverbonSignedBadge ? <span className="order-tab-notification-dot" aria-hidden /> : null}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={viewTab === 'servicebon'}
                className={`order-tab${servicebonTabBadge ? ' order-tab--has-badge' : ''}`}
                title={servicebonTabTooltip}
                aria-label={servicebonTabAria}
                onClick={() => setViewTab('servicebon')}
              >
                {t('servicebonLabel')}
                {servicebonTabBadge ? <span className="order-tab-notification-dot" aria-hidden /> : null}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={viewTab === 'files'}
                className="order-tab"
                onClick={() => setViewTab('files')}
              >
                {t('filesLabel')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={viewTab === 'notes'}
                className={`order-tab${notesTabBadge ? ' order-tab--has-badge' : ''}`}
                title={notesTabBadge ? notesTabTitle.replace(`${t('notesLabel')}. `, '') : undefined}
                aria-label={notesTabTitle}
                onClick={() => setViewTab('notes')}
              >
                {t('notesLabel')}
                {notesTabBadge ? <span className="order-tab-notification-dot" aria-hidden /> : null}
              </button>
            </div>
          }
          headerActions={
            <div className="flex items-center gap-2">
              <button type="button" onClick={handlePrintOpleverbon} className="vmk-btn vmk-btn-primary">
                {viewTab === 'servicebon' ? `${t('printLabel')} ${t('servicebonLabel')}` : `${t('printLabel')} ${t('completionSlipLabel')}`}
              </button>
            </div>
          }
        />
        <div className="order-tabpanel mt-5 rounded-2xl border border-[#dbe4f0] bg-gradient-to-b from-[#f9fbff] to-white p-5 shadow-sm">
          {viewTab === 'voorblad' && (
            <div className="bg-white rounded-lg shadow-lg border border-[#e2e8f0] mx-auto" style={{ maxWidth: '210mm' }}>
              <VoorbladDocument order={viewOrder} contentRef={printRef} />
            </div>
          )}
          {viewTab === 'notes' && (
            <div className="space-y-4">
              <section className="vmk-card-flat p-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-[#94a3b8] mb-2">{t('statusLabel')}</h4>
                <div className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 dark:border-[#334155] dark:bg-[#0f172a]">
                  {(() => {
                    const workflow = getWorkflowStatus(viewOrder);
                    return <span className={`status-badge ${workflow.key}`}>{workflow.label}</span>;
                  })()}
                </div>
              </section>

              <section className="vmk-card-flat p-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-[#94a3b8] mb-2">{t('plannerNotesLabel')}</h4>
                <div className="rounded-lg border border-[#e2e8f0] bg-white p-3 dark:border-[#334155] dark:bg-[#0f172a]">
                  <p className="text-sm text-[#334155] whitespace-pre-wrap dark:text-[#cbd5e1]">
                    {viewOrder.notes || t('noPlannerNote')}
                  </p>
                </div>
              </section>

              <section className="vmk-card-flat p-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-[#94a3b8] mb-2">{t('workerNotesLabel')}</h4>
                {workerNotes.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-[#cbd5e1] bg-[#f8fafc] p-3 dark:border-[#334155] dark:bg-[#0f172a]">
                    <p className="text-sm text-[#64748b]">{t('noWorkerNotesYet')}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {[...workerNotes].reverse().map((entry, idx) => (
                      <div key={`${entry.timestamp || idx}`} className="rounded-lg border border-[#e2e8f0] bg-white p-3 dark:border-[#334155] dark:bg-[#0f172a]">
                        <p className="text-sm whitespace-pre-wrap">{entry.text || ''}</p>
                        <p className="text-xs text-[#94a3b8] mt-1">
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleString(language === 'en' ? 'en-GB' : 'nl-NL') : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}
          {viewTab === 'files' && (
            <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
              <OrderFilesPanel
                orderId={viewOrder.id}
                orderTitle={[viewOrder.client_name, viewOrder.address].filter(Boolean).join(' — ')}
                mobilePhotosJson={viewOrder.mobile_photos_json}
                onRequestDeleteMobilePhoto={(url) => setDeletePhotoUrl(url)}
                t={t}
                language={language}
              />
              <NetworkFilesPanel t={t} />
            </div>
          )}
          {(viewTab === 'opleverbon' || viewTab === 'servicebon') && (
            <div
              className={showServicebonNav ? 'order-servicebon-shell' : 'flex w-full justify-center'}
            >
              <div className={showServicebonNav ? 'order-servicebon-shell__doc' : ''}>
                <div
                  className="rounded-lg shadow-lg border border-[#e2e8f0] mx-auto"
                  style={{
                    width: '210mm',
                    maxWidth: '210mm',
                    minHeight: '297mm',
                    height: '297mm',
                    backgroundColor: '#fff',
                    color: '#111',
                  }}
                >
                  <div
                    ref={opleverbonPrintRef}
                    className={`print-content slip-print-root slip-a4-page${
                      isServicebonView ? ' slip-a4-page--servicebon' : ' slip-a4-page--opleverbon'
                    }`}
                    style={{
                      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                      backgroundColor: '#fff',
                      color: '#111',
                    }}
                  >
                <div
                  className="slip-a4-header slip-a4-fixed"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, auto) 1fr minmax(0, auto)',
                    alignItems: 'center',
                    columnGap: '12px',
                  }}
                >
                  <img
                    src={printLogoSrc()}
                    alt="VMK Kozijnen"
                    style={{ height: '50px', width: 'auto', maxWidth: '225px', objectFit: 'contain', display: 'block' }}
                  />
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: isServicebonView ? '18px' : '17px', fontWeight: 600, color: '#222' }}>
                      {viewTab === 'servicebon' ? t('servicebonLabel') : t('completionSlipLabel')}
                    </span>
                    {viewTab === 'servicebon' ? (
                      <span
                        style={{
                          display: 'table',
                          margin: '6px auto 0',
                          fontSize: '10px',
                          fontWeight: 600,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          color: viewingServicebonArchive ? '#0369a1' : '#047857',
                          background: viewingServicebonArchive ? '#e0f2fe' : '#d1fae5',
                          border: viewingServicebonArchive ? '1px solid #7dd3fc' : '1px solid #6ee7b7',
                          borderRadius: '999px',
                          padding: '4px 10px',
                        }}
                      >
                        {viewingServicebonArchive
                          ? `${t('servicebonArchiveRibbonPrefix')} · ${formatServicebonRoundLabel(
                              servicebonHistoryList[servicebonArchiveIndex],
                              servicebonArchiveIndex + 1,
                              viewOrder,
                            )}`
                          : `${t('servicebonCurrentRibbonPrefix')} · ${currentServicebonRoundLabel}`}
                      </span>
                    ) : null}
                  </div>
                  <div style={{ width: '48px' }} aria-hidden="true" />
                </div>
                <div
                  className="slip-a4-block slip-a4-fixed"
                  style={{ ...slipBoxStyle, padding: isServicebonView ? '8px 9px' : '8px 9px' }}
                >
                  {[
                    [t('opleverbonClientName'), viewOrder.client_name || ''],
                    [t('opleverbonAddress'), viewOrder.address || ''],
                    [t('opleverbonPcPlaats'), viewOrder.postcode || ''],
                    [t('opleverbonPhoneResident'), viewOrder.phone || ''],
                    [t('opleverbonInstallers'), teamMemberNames],
                  ].map(([label, value]) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', minHeight: isServicebonView ? '26px' : '23px' }}>
                      <span style={{ width: '140px', fontSize: isServicebonView ? '13px' : '12px', fontWeight: 600, color: '#1f2937' }}>{label}</span>
                      <span style={{ margin: '0 8px', fontSize: isServicebonView ? '13px' : '12px' }}>:</span>
                      <div
                        style={{
                          flex: 1,
                          borderBottom: '1.5px solid #222',
                          fontSize: isServicebonView ? '13px' : '12px',
                          lineHeight: isServicebonView ? '21px' : '19px',
                          minHeight: isServicebonView ? '21px' : '19px',
                        }}
                      >
                        {value}
                      </div>
                    </div>
                  ))}
                </div>

                {isServicebonView ? (
                  <div
                    className="slip-a4-block slip-a4-declaration slip-a4-fixed"
                    style={{ ...slipBoxStyle, padding: '10px' }}
                  >
                    <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
                      {language === 'en' ? 'Dear Sir/Madam,' : 'Geachte heer/mevrouw,'}
                    </div>
                    <div style={{ fontSize: '11px', lineHeight: 1.35, whiteSpace: 'pre-wrap' }}>
                      {language === 'en'
                        ? SERVICEBON_DECLARATION_TEXT_EN.split('\n\n').slice(1).join('\n\n')
                        : SERVICEBON_DECLARATION_TEXT.split('\n\n').slice(1).join('\n\n')}
                    </div>
                  </div>
                ) : (
                  <div
                    className="slip-a4-block slip-a4-declaration slip-a4-fixed"
                    style={{ ...slipBoxStyle, padding: '8px 9px' }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>{language === 'en' ? 'Dear Sir/Madam,' : 'Geachte heer/mevrouw,'}</div>
                    <div style={{ fontSize: '10px', lineHeight: 1.25, whiteSpace: 'pre-wrap' }}>
                      {language === 'en' ? OPLEVERBON_DECLARATION_TEXT_EN : OPLEVERBON_DECLARATION_TEXT}
                    </div>
                  </div>
                )}

                {isServicebonView ? (
                <div className="slip-work-section">
                  <div
                    className="slip-work-section-box"
                    style={{ ...slipBoxStyle, padding: '10px' }}
                  >
                    <div className="slip-work-section-title">{t('workCarriedOutLabel')}</div>
                  {!viewingFromClosed &&
                  canEditServicebonWorkOnWeb(viewOrder) &&
                  servicebonArchiveIndex == null ? (
                    <div className="servicebon-work-editor slip-print-work-editor slip-work-section-body">
                      <div className="no-print slip-work-editor-chrome">
                        <textarea
                          ref={servicebonWorkTextareaRef}
                          className="servicebon-work-draft-field slip-work-textarea"
                          value={servicebonWorkDraft}
                          onChange={(e) => setServicebonWorkDraft(e.target.value)}
                          onFocus={() => {
                            if (servicebonWorkBlurTimerRef.current) {
                              clearTimeout(servicebonWorkBlurTimerRef.current);
                              servicebonWorkBlurTimerRef.current = null;
                            }
                          }}
                          onBlur={() => {
                            servicebonWorkBlurTimerRef.current = setTimeout(() => {
                              servicebonVoice.stop();
                            }, 350);
                          }}
                          spellCheck
                        />
                        {(() => {
                          const dirty = servicebonWorkDraft !== servicebonDraftBaseline;
                          const showSavedEdit = !dirty && servicebonDraftSavedOnce && !servicebonDraftSaving;
                          if (showSavedEdit) {
                            return (
                              <button
                                type="button"
                                className="vmk-btn mt-2 text-sm py-2 inline-flex items-center gap-2 border border-[#00cba9] bg-white text-[#0a5c4a] hover:bg-[#e8fdf8] dark:border-[#00cba9]/60 dark:bg-[#0f172a] dark:text-[#7ee8d3]"
                                onClick={() => servicebonWorkTextareaRef.current?.focus()}
                              >
                                <PencilSimple size={18} weight="bold" aria-hidden />
                                {t('servicebonWorkDraftEdit')}
                              </button>
                            );
                          }
                          return (
                            <button
                              type="button"
                              className="vmk-btn vmk-btn-primary mt-2 text-sm py-2"
                              disabled={servicebonDraftSaving}
                              onClick={() => void handleSaveServicebonWorkDraft()}
                            >
                              {servicebonDraftSaving ? t('saving') : t('servicebonWorkDraftSave')}
                            </button>
                          );
                        })()}
                        <p className="text-xs text-[#64748b] mt-2">{t('servicebonWorkDraftHint')}</p>
                      </div>
                      <div className="servicebon-print-fill" style={{ display: 'none' }}>
                        {servicebonWorkDraft}
                      </div>
                    </div>
                  ) : (
                    <div className="slip-work-section-body slip-print-work-body">
                      <div style={{ paddingTop: '2px' }}>{slip?.pending_work || ''}</div>
                    </div>
                  )}
                  </div>
                </div>
                ) : (
                <div className="slip-a4-expand-middle">
                  <div className="slip-work-section">
                    <div
                      className="slip-work-section-box"
                      style={{ ...slipBoxStyle, padding: '8px 9px' }}
                    >
                      <div className="slip-work-section-title">{t('remainingWorkLabel')}</div>
                      <div className="slip-work-section-body slip-print-work-body">
                        <div style={{ paddingTop: '2px' }}>{slip?.pending_work || ''}</div>
                      </div>
                    </div>
                  </div>
                  <div className="slip-materials-section">
                    <div
                      className="slip-materials-section-box"
                      style={{ ...slipBoxStyle, padding: '8px 9px' }}
                    >
                      <div className="slip-materials-section-title">{t('requiredMaterialsLabel')}:</div>
                      <div style={{ fontSize: '10px', marginBottom: '3px', lineHeight: 1.25, flexShrink: 0 }}>
                        {t('requiredMaterialsInfo')}
                      </div>
                      <div className="slip-materials-section-body slip-print-materials-body">
                        <div style={{ paddingTop: '2px' }}>{signature?.required_materials || ''}</div>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {isServicebonView ? (
                  <div className="slip-print-footer-block" style={{ ...slipBoxStyle, padding: '10px 10px' }}>
                    {slipFooter}
                  </div>
                ) : (
                  <div className="slip-print-footer-block" style={{ ...slipBoxStyle, padding: '8px 9px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, marginBottom: '3px' }}>{t('financialSettlementLabel')}</div>
                    <div style={{ fontSize: '10px', lineHeight: 1.25, marginBottom: '4px', whiteSpace: 'pre-wrap' }}>
                      {language === 'en'
                        ? 'Under our general terms, if the project is not fully completed at handover due to documented remaining items, up to 10% of the invoice amount may be withheld until those items are properly resolved, unless otherwise agreed in writing. The remainder of the invoice must be paid according to the agreed payment term.'
                        : 'Conform onze algemene voorwaarden geldt dat, indien het project bij oplevering nog niet volledig is afgerond wegens schriftelijk vastgelegde restpunten, maximaal 10% van het factuurbedrag mag worden ingehouden tot het moment waarop deze restpunten naar behoren zijn afgehandeld, tenzij schriftelijk anders is overeengekomen. Het resterende factuurbedrag dient conform de overeengekomen betalingstermijn te worden voldaan.'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: '6px', columnGap: '18px', marginBottom: '6px', marginTop: '4px', fontSize: '11px' }}>
                      <span style={{ fontWeight: 600, whiteSpace: 'nowrap', marginRight: '4px' }}>{t('opleverbonInvoiceHold')}:</span>
                      {[
                        ['nee', t('opleverbonInvoiceHoldNee')],
                        ['10', t('opleverbonInvoiceHoldJa10')],
                      ].map(([key, label]) => (
                        <span key={key} style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', paddingLeft: '4px', paddingRight: '4px' }}>
                          <span style={printCheckbox(financialHold.key === key)}>{financialHold.key === key ? '✓' : ''}</span>
                          <span style={{ marginLeft: '2px' }}>{label}</span>
                        </span>
                      ))}
                      <span style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', paddingLeft: '4px', paddingRight: '4px' }}>
                        <span style={printCheckbox(financialHold.key === 'custom')}>{financialHold.key === 'custom' ? '✓' : ''}</span>
                        <span style={{ marginLeft: '2px', marginRight: '4px' }}>{t('opleverbonInvoiceHoldJaPrefix')}</span>
                        <span
                          style={{
                            minWidth: '22px',
                            borderBottom: '1.5px solid #222',
                            textAlign: 'center',
                            fontSize: '11px',
                            lineHeight: '14px',
                            padding: '0 4px',
                          }}
                        >
                          {financialHold.key === 'custom' && financialHold.percent != null ? financialHold.percent : ''}
                        </span>
                        <span style={{ marginLeft: '1px' }}>%</span>
                        <span style={{ marginLeft: '4px', fontSize: '9px', fontWeight: 500, color: '#444' }}>({t('opleverbonInvoiceHoldPctMax')})</span>
                      </span>
                    </div>
                    {slipFooter}
                  </div>
                )}
              </div>
            </div>
              </div>
              {showServicebonNav && (
                <aside className="order-servicebon-nav" role="navigation" aria-label={t('servicebonNavAria')}>
                  <p className="order-servicebon-nav__title">{t('servicebonNavTitle')}</p>
                  <ul className="order-servicebon-nav__list">
                    <li>
                      <button
                        type="button"
                        className={servicebonArchiveIndex == null ? 'is-active' : undefined}
                        onClick={() => setServicebonArchiveIndex(null)}
                      >
                        <span className="order-servicebon-nav__btn-label">
                          <FileText size={16} weight="duotone" className="order-servicebon-nav__icon" aria-hidden />
                          {t('servicebonNavCurrent')} · {currentServicebonRoundLabel}
                        </span>
                        <span className="order-servicebon-nav__meta">
                          {(() => {
                            const sb =
                              signature?.servicebon && typeof signature.servicebon === 'object'
                                ? signature.servicebon
                                : null;
                            if (sb && String(sb.data || '').trim()) return t('servicebonNavSignedShort');
                            return t('servicebonNavDraftShort');
                          })()}
                        </span>
                      </button>
                    </li>
                    {servicebonHistoryList.map((entry, idx) => {
                      const metaParts = [];
                      if (entry.signed_at) {
                        metaParts.push(
                          new Date(entry.signed_at).toLocaleDateString(language === 'en' ? 'en-GB' : 'nl-NL'),
                        );
                      }
                      if (entry.signer_name) metaParts.push(entry.signer_name);
                      return (
                        <li key={`${entry.signed_at || ''}-${idx}`}>
                          <button
                            type="button"
                            className={servicebonArchiveIndex === idx ? 'is-active' : undefined}
                            onClick={() => setServicebonArchiveIndex(idx)}
                          >
                            <span className="order-servicebon-nav__btn-label">
                              <Archive size={16} weight="duotone" className="order-servicebon-nav__icon" aria-hidden />
                              {t('servicebonArchiveRibbonPrefix')} ·{' '}
                              {formatServicebonRoundLabel(entry, idx + 1, viewOrder)}
                            </span>
                            <span className="order-servicebon-nav__meta">{metaParts.join(' · ')}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </aside>
              )}
            </div>
          )}
        </div>
        <AlertDialog open={!!deletePhotoUrl} onOpenChange={(open) => { if (!open) setDeletePhotoUrl(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('photoRemoveTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('photoRemoveMessage')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('photoRemoveCancel')}</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600 dark:bg-red-600 dark:hover:bg-red-700"
                onClick={(e) => {
                  e.preventDefault();
                  void handleConfirmDeleteMobilePhoto();
                }}
              >
                {t('photoRemoveConfirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {servicebonVoiceControls}
      </div>
    );
  }

  if (editOrder) {
    const editOfferStub = {
      id: editOrder.offer_id,
      offer_number: editOrder.offer_id,
      contact: {
        name: editOrder.client_name,
        address: editOrder.address,
        postcode: editOrder.postcode,
        phone: editOrder.phone,
        email: editOrder.email,
      },
    };
    return (
      <div data-testid="orders-page" className="animate-fadeIn">
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setEditOrder(null)}
            className="vmk-btn"
          >
            {t('backToOrdersList')}
          </button>
        </div>
        <div className="vmk-card p-6">
          <h2 className="text-xl font-bold mb-1">{t('editOrderPageTitle')}</h2>
          <p className="text-sm text-[#64748b] mb-6">{t('editOrderPageSubtitle')}</p>
          <WerkvoorbereidingForm
            offer={editOfferStub}
            initialData={editOrder}
            mode="edit"
            orderId={editOrder.id}
            onClose={() => setEditOrder(null)}
            onSuccess={() => {
              toast.success(t('orderUpdatedSuccess'));
              setEditOrder(null);
              fetchData();
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="orders-page" className="animate-fadeIn">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="page-title">{t('orders')}</h1>
          <p className="page-subtitle">
            {t('manageOrders')} • {activeOrders.length} {t('total')}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative min-w-0 flex-1 max-w-md">
          <MagnifyingGlass
            size={18}
            className="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2 text-[#94a3b8]"
            aria-hidden
          />
          <input
            type="text"
            placeholder={t('searchOrders')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="vmk-input !pl-[2.75rem] !pr-4"
            data-testid="search-orders"
          />
        </div>
      </div>

      {activeOrders.length === 0 ? (
        <div className="vmk-card text-center py-16">
          <div className="w-16 h-16 rounded-full bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
            <ClipboardText size={32} className="text-[#94a3b8]" />
          </div>
          <h3 className="text-lg font-semibold text-[#1a1a1a] mb-2">{t('noOrdersYet')}</h3>
          <p className="text-[#64748b]">{t('createOrdersFromOffers')}</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="vmk-table">
            <thead>
              <tr>
                <th>{t('client')}</th>
                <th>{t('address')}</th>
                <th>{t('status')}</th>
                <th>{t('team')}</th>
                <th>{t('scheduled')}</th>
                <th>{t('track')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id} data-testid={`order-row-${order.id}`}>
                  <td>
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold">{order.client_name}</span>
                      {orderNeedsAttention(order) ? (
                        <span
                          className="orders-list-attention-dot"
                          title={buildOrderAttentionTitle(order, t)}
                          aria-label={buildOrderAttentionTitle(order, t)}
                          role="img"
                        />
                      ) : null}
                    </span>
                  </td>
                  <td>
                    <div>
                      <div className="text-sm">{order.address}</div>
                      <div className="text-xs text-[#94a3b8]">{order.postcode}</div>
                    </div>
                  </td>
                  <td>
                    {(() => {
                      const workflow = getWorkflowStatus(order);
                      return (
                        <span className={`status-badge ${workflow.key}`}>
                          {workflow.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    {order.assigned_team_id ? (
                      <div className="flex items-center gap-2">
                        <div 
                          className="team-color" 
                          style={{ backgroundColor: getTeamColor(order.assigned_team_id) }}
                        />
                        <span className="text-sm font-medium">{getTeamName(order.assigned_team_id)}</span>
                      </div>
                    ) : (
                      <span className="text-[#94a3b8]">—</span>
                    )}
                  </td>
                  <td>
                    <span className="font-mono text-sm">
                      {order.scheduled_date || order.montagedatum || '—'}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      {MARKER_COLORS.map((marker) => {
                        const isSelected = String(order.planning_marker_color || '').toLowerCase() === marker.key;
                        return (
                          <button
                            key={`${order.id}-${marker.key}`}
                            type="button"
                            onClick={() => handleSetPlanningMarkerColor(order, marker.key)}
                            className={`planning-marker-radio ${isSelected ? 'selected' : ''}`}
                            style={{ '--marker-color': marker.hex }}
                            title={`${t('track')} ${marker.key}`}
                            aria-label={`${t('track')} ${marker.key}`}
                            data-testid={`order-marker-${order.id}-${marker.key}`}
                          />
                        );
                      })}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      {/* View Button */}
                      <button
                        onClick={() => handleViewOrder(order)}
                        className="vmk-btn text-sm py-2"
                        data-testid={`view-btn-${order.id}`}
                        title={t('viewPrintOrderTitle')}
                      >
                        <Eye size={16} />
                        {t('viewOrder')}
                      </button>
                      <button
                        onClick={() => handleEditOrder(order)}
                        className="vmk-btn text-sm py-2"
                        data-testid={`edit-btn-${order.id}`}
                        title={t('editOrderTitleAttr')}
                      >
                        <PencilSimple size={16} />
                        {t('editOrder')}
                      </button>
                      {canMarkComplete &&
                      String(order.mobile_work_status || '') === 'completed' &&
                      order.pending_closed_release ? (
                        <button
                          type="button"
                          onClick={() => void handleCompleteAfterBalance(order)}
                          className="vmk-btn text-sm py-2 border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-100 dark:bg-amber-950/50 dark:hover:bg-amber-950/70"
                          data-testid={`finalize-balance-btn-${order.id}`}
                          title={t('markOrderCompleteAfterBalance')}
                        >
                          {t('markOrderCompleteAfterBalance')}
                        </button>
                      ) : null}
                      {order.status === 'unassigned' ? (
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowAssign(true);
                          }}
                          className="vmk-btn vmk-btn-primary text-sm py-2"
                          data-testid={`assign-btn-${order.id}`}
                        >
                          <UserPlus size={16} />
                          {t('assign')}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUnassign(order.id)}
                          className="vmk-btn text-sm py-2"
                          data-testid={`unassign-btn-${order.id}`}
                        >
                          {t('unassign')}
                        </button>
                      )}
                      {canMarkComplete ? (
                        <button
                          onClick={() => handleDelete(order.id)}
                          className="vmk-btn-ghost p-2 rounded-lg text-[#ef4444] hover:bg-red-50"
                          data-testid={`delete-btn-${order.id}`}
                          title={t('confirmDeleteOrder')}
                          aria-label={t('confirmDeleteOrder')}
                        >
                          <Trash size={18} />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Assign Dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="vmk-dialog-content max-w-md">
          <DialogHeader className="px-6 py-5 border-b border-[#e2e8f0] bg-[#f8f9fb]">
            <DialogTitle className="text-lg font-bold">{t('assignOrder')}</DialogTitle>
            <DialogDescription className="sr-only">{t('assignOrder')}</DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-5">
            <div>
              <label className="form-label mb-2 block">{t('client')}</label>
              <p className="font-semibold text-lg">{selectedOrder?.client_name}</p>
              <p className="text-sm text-[#64748b]">{selectedOrder?.address}</p>
            </div>
            
            <div>
              <label className="form-label mb-2 block">{t('selectTeam')}</label>
              <Select
                value={assignData.team_id}
                onValueChange={(value) => setAssignData(prev => ({ ...prev, team_id: value }))}
              >
                <SelectTrigger className="vmk-input h-12" data-testid="assign-team-select">
                  <SelectValue placeholder={t('selectTeam')} />
                </SelectTrigger>
                <SelectContent>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      <div className="flex items-center gap-3">
                        <div
                          className="team-color"
                          style={{ backgroundColor: team.color }}
                        />
                        <span>{team.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowAssign(false)}
                className="vmk-btn flex-1"
                data-testid="assign-cancel-btn"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleAssignOrder}
                className="vmk-btn vmk-btn-primary flex-1"
                data-testid="assign-confirm-btn"
              >
                {t('assignOrder')}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default OrdersPage;
